/**
 * Stripe.js, loaded from Stripe — never bundled, never an npm dependency.
 *
 * Stripe requires it: the card fields live in Stripe's own iframes, served
 * from `js.stripe.com`, which is what keeps a card number out of this app's
 * JavaScript entirely (and out of PCI scope). A copy in our bundle would be a
 * copy Stripe cannot update and a reviewer cannot trust.
 *
 * **Loaded only when somebody is about to pay.** The script is ~100 KB and
 * talks to Stripe on load; a visitor opening a free lineup must never fetch it.
 *
 * Only the handful of calls the paywall makes are typed here, deliberately —
 * the surface we depend on is the surface a Stripe upgrade could break.
 */

export const STRIPE_JS_URL = 'https://js.stripe.com/v3';

/**
 * Stripe's EMBEDDED Checkout — its whole payment form, drawn in its iframe
 * inside our page. A Checkout Session rather than a bare Payment Element
 * since 2026-10-07: prices are in US dollars and each buyer pays in their own
 * currency (the user's decision), which is Stripe's Adaptive Pricing — and
 * Stripe offers that only on Checkout Sessions. The form shows the buyer's
 * currency and its own pay button.
 */
export interface StripeEmbeddedCheckout {
  mount(target: HTMLElement): void;
  destroy(): void;
}

export interface StripeInstance {
  initEmbeddedCheckout(options: {
    fetchClientSecret: () => Promise<string>;
    /** Called when a payment finishes in place (`redirect_on_completion: if_required`). */
    onComplete?: () => void;
  }): Promise<StripeEmbeddedCheckout>;
}

type StripeFactory = (publishableKey: string) => StripeInstance;

declare global {
  interface Window {
    Stripe?: StripeFactory;
  }
}

let loading: Promise<StripeFactory> | null = null;

/**
 * The `Stripe` factory, loading the script once per page.
 *
 * A failed load clears the memo, so "try again" after a dropped connection
 * really tries again instead of replaying the same rejection.
 */
export function loadStripe(): Promise<StripeFactory> {
  if (typeof window !== 'undefined' && window.Stripe) return Promise.resolve(window.Stripe);
  if (loading) return loading;

  loading = new Promise<StripeFactory>((resolve, reject) => {
    const fail = () => {
      loading = null;
      reject(new Error('The card form could not be loaded. Check your connection and try again.'));
    };
    const existing = document.querySelector<HTMLScriptElement>(`script[src="${STRIPE_JS_URL}"]`);
    const script = existing ?? document.createElement('script');
    script.addEventListener('load', () => (window.Stripe ? resolve(window.Stripe) : fail()));
    script.addEventListener('error', fail);
    if (!existing) {
      script.src = STRIPE_JS_URL;
      script.async = true;
      document.head.appendChild(script);
    }
  });
  return loading;
}
